import PropTypes from 'prop-types'
import React from 'react'
import { NetworkStatus } from '@apollo/client'

import { CartButton } from '@local/do-secundo-cart-button'
import { useGetCart } from '../CartQuery/CartQuery'
import { Dollars } from '@local/do-secundo-dollars'
import { EmptyCart } from '../EmptyCart/EmptyCart'
import { ErrorComponent } from '@local/do-secundo-error'

import { ApprovalRules } from './ApprovalRules/ApprovalRules'
import { useApprovalRulesValidator } from './ApprovalRules/use-approval-rules-validator'
import { CartLoader } from './Cart.loader'
import { CartFooter } from './CartFooter/CartFooter'
import { CartTable } from './CartTable/CartTable'
import { useReorder } from '../Reorder/ReorderProvider/ReorderProvider'
import { CartModificationError } from './CartModificationError/CartModificationError'
import { CartUpsells } from '../Upsells/CartUpsells'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import { useFlag, FF } from '@local/do-secundo-feature-flag'

import styles from './Cart.module.css'

export const Cart = React.memo(
  ({ checkoutLink, orderingAvailable, rectifyApprovalRuleLink, menuLink }) => {
    const { error, loading, refetch, networkStatus, cart, warnings } =
      useGetCart()
    const { restaurantInfo } = useRestaurant()
    let upsellsEnabled = false
    const GLOBAL_GC_ENABLED = useFlag(FF.GLOBAL_GIFTCARDS)

    if (
      restaurantInfo.data &&
      restaurantInfo.data.restaurant &&
      restaurantInfo.data.restaurant.upsellsConfig
    ) {
      upsellsEnabled = Boolean(
        restaurantInfo.data.restaurant.upsellsConfig.enabled
      )
    }

    const cartIsEmpty = !cart || cart.order.numberOfSelections === 0

    // we need to do this calcuation here since global rewards don't modify order amount the same way
    // restaurant promos do
    let cartTotal = 0
    if (!cartIsEmpty) {
      cartTotal = cart.order?.total
      if (cart.order?.discounts.globalReward && GLOBAL_GC_ENABLED) {
        cartTotal = cartTotal - cart.order?.discounts.globalReward.amount
      }
    }

    const { valid: approvalRulesValid } = useApprovalRulesValidator({ cart })
    const {
      reorderMutation: [, { loading: reorderLoading }]
    } = useReorder()
    const cartHasWarnings = warnings && warnings.length > 0

    const checkoutButtonEnabled =
      orderingAvailable && approvalRulesValid && !cartHasWarnings

    if (loading || networkStatus === NetworkStatus.refetch) {
      return <CartLoader />
    }

    if (error) {
      return <ErrorComponent error={error} retry={refetch} />
    }

    return (
      <>
        <CartModificationError />
        {cartIsEmpty ? (
          <EmptyCart
            menuLink={menuLink}
            orderingAvailable={orderingAvailable}
          />
        ) : (
          <>
            <div className={styles.long}>
              <CartTable cart={cart} editable />
            </div>
            <div className={styles.sticky}>
              {upsellsEnabled && <CartUpsells />}
              <CartFooter cart={cart} deliveryChargeLabel='Delivery Estimate' />
              <ApprovalRules
                cart={cart}
                rectifyApprovalRuleLink={rectifyApprovalRuleLink}
              />
              <CartButton
                data-testid='cart-checkout-button'
                to={checkoutLink}
                left={<span>Checkout ({cart.order.numberOfSelections})</span>}
                right={<Dollars amount={cartTotal} />}
                loading={reorderLoading}
                disabled={!checkoutButtonEnabled || reorderLoading}
              />
            </div>
          </>
        )}
      </>
    )
  }
)

Cart.displayName = 'Cart'

Cart.propTypes = {
  checkoutLink: PropTypes.string,
  orderingAvailable: PropTypes.bool,
  /**
   * If present, this path indicates where the user must navigate in order to rectify
   * an approval rule that is currently violated.
   */
  rectifyApprovalRuleLink: PropTypes.string
}
